import { useMemo } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { faPencilAlt } from "@fortawesome/free-solid-svg-icons/faPencilAlt";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import {
    AXNUIContent,
    AXNUIContentMockRPDefault,
    AXNUILayout
} from "@iddataweb/idw-common-ui/types";
import {
    isStringTrue,
    getLandingPageContentAndLayout,
    getRedirectUrlWithoutHostname
} from "@iddataweb/idw-common-ui/utils";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { resources } from "@constants/hardcoded-strings";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { TextareaFormGroup } from "@containers/common/textarea-form-group/textarea-form-group";
import { ThemingFormSection } from "@containers/common/theming-form-section/theming-form-section";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { RadioInputFormGroup } from "@containers/common/radio-input-form-group/radio-input-form-group";
import { ModifyLandingPageNameModal } from "@containers/modals/modify-landing-page-name-modal/modify-landing-page-name-modal";
import { ClickableIcon } from "@containers/common/clickable-icon/clickable-icon";
import { useModalContext } from "@context/use-modal-context";
import { ThemingLandingPageValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { LayoutTypes } from "@enums/layout-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { LandingPageTypes } from "@enums/landing-page-types";
import { ModalTypes } from "@enums/modal-types";
import styles from "./custom-landing-page-container.less";
import { RedirectUrlOriginTypes } from "@enums/redirect-url-origin-types";
import { RedirectUrlOriginBadge } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-landing-page-tab-container/redirect-url-origin-badge/redirect-url-origin-badge";

type CustomLandingPageContainerProps = {
    isTheming: boolean;
    showOriginBadge: boolean
    redirectUrl: string;
    redirectUrlOrigin: RedirectUrlOriginTypes;
    index: number;
    isDisabled: boolean;
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    pageLevel: PageLevelTypes;
    axnuiContent: AXNUIContent;
    axnuiLayout: AXNUILayout;
    validationErrors: ThemingLandingPageValidationErrors;
    inheritedAXNUIContent: AXNUIContent;
    inheritedAXNUILayout: AXNUILayout;
    handleRemoveRedirect: (index: number, redirectUrlOrigin?: RedirectUrlOriginTypes) => void;
    handleRedirectChangeAtIndex: (newRedirectUrl: string, index: number, redirectUrl: string, redirectUrlOrigin: RedirectUrlOriginTypes) => void;
    updateAXNUIContentPropertyValue: (axnuiContent: AXNUIContent) => void;
    updateAXNUILayoutPropertyValue: (axnuiLayout: AXNUILayout) => void;
};

export const CustomLandingPageContainer = (props: CustomLandingPageContainerProps) => {
    const {
        isTheming,
        showOriginBadge,
        redirectUrl,
        redirectUrlOrigin,
        index,
        isDisabled,
        validationErrors,
        pageLevel,
        layout,
        labelWidth,
        axnuiContent,
        axnuiLayout,
        inheritedAXNUIContent,
        inheritedAXNUILayout,
        handleRemoveRedirect,
        handleRedirectChangeAtIndex,
        updateAXNUIContentPropertyValue,
        updateAXNUILayoutPropertyValue
    } = props;

    const modalContext = useModalContext();

    const {
        approveMessageHTML: inheritedApproveMessageHTML,
        asiLabel: inheritedAsiLabel,
        denyMessageHTML: inheritedDenyMessageHTML,
        showAsiLabel: inheritedShowAsiLabel
    } = useMemo(() => {
        if (inheritedAXNUIContent && inheritedAXNUILayout) {
            return getLandingPageContentAndLayout(inheritedAXNUIContent, inheritedAXNUILayout, redirectUrl);
        }

        return {
            approveMessageHTML: null,
            asiLabel: null,
            denyMessageHTML: null,
            showAsiLabel: null
        } as {
            showAsiLabel: boolean;
            asiLabel: string;
            approveMessageHTML: string;
            denyMessageHTML: string;
        };
    }, [inheritedAXNUIContent, inheritedAXNUILayout, redirectUrl]);

    const {
        approveMessageHTML,
        asiLabel,
        denyMessageHTML,
        showAsiLabel
    } = useMemo(() => {
        if (axnuiContent && axnuiLayout) {
            return getLandingPageContentAndLayout(axnuiContent, axnuiLayout, redirectUrl)
        }

        return {
            approveMessageHTML: null,
            asiLabel: null,
            denyMessageHTML: null,
            showAsiLabel: null
        } as {
            showAsiLabel: boolean;
            asiLabel: string;
            approveMessageHTML: string;
            denyMessageHTML: string;
        };
    }, [axnuiContent, axnuiLayout, redirectUrl]);

    const redirectUrlForDisplay = useMemo(() => {
        return getRedirectUrlWithoutHostname(redirectUrl);
    }, [redirectUrl]);

    const updateAXNUIContentMockRPFieldValue = (
        field: keyof AXNUIContentMockRPDefault,
        value: string
    ) => {

        const mockrpContent: AXNUIContentMockRPDefault = {
            approveMessageHTML: axnuiContent.mockRP[redirectUrl]?.approveMessageHTML || undefined,
            denyMessageHTML: axnuiContent.mockRP[redirectUrl]?.denyMessageHTML || undefined,
            asiLabel: axnuiContent.mockRP[redirectUrl]?.asiLabel || undefined,
            noDecisionMessage: axnuiContent.mockRP.default.noDecisionMessage || undefined
        };

        if (value !== axnuiContent.mockRP.default[field]) {
            mockrpContent[field] = value;
        } else {
            mockrpContent[field] = undefined;
        }

        const newPropertyValue: AXNUIContent = {
            ...axnuiContent,
            mockRP: {
                ...axnuiContent.mockRP,
                [redirectUrl]: {
                    ...mockrpContent
                }
            }
        };
        updateAXNUIContentPropertyValue(newPropertyValue);
    };

    const handleClickDeleteRedirectUrl = () => {
        handleRemoveRedirect(index, redirectUrlOrigin);
    };

    const handleClickModifyLandingPageName = () => {
        const onClickContinue = (newRedirectUrl: string) => {
            handleRedirectChangeAtIndex(newRedirectUrl, index, redirectUrl, redirectUrlOrigin);
        };

        const renderModifyLandingPageName = (onModalClose: () => void) => {
            return (
                <ModifyLandingPageNameModal
                    landingPageType={LandingPageTypes.CUSTOM}
                    landingPageName={redirectUrl}
                    onClickContinue={onClickContinue}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.ModifyLandingPageName,
            renderFunction: renderModifyLandingPageName
        });
    };

    const handleApproveMessageHTMLChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentMockRPFieldValue("approveMessageHTML", event.target.value);
    };

    const handleResetApproveMessageHTML = () => {
        updateAXNUIContentMockRPFieldValue("approveMessageHTML", inheritedAXNUIContent.mockRP.default.approveMessageHTML);
    };

    const handleDenyMessageHTMLChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentMockRPFieldValue("denyMessageHTML", event.target.value);
    };

    const handleResetDenyMessageHTML = () => {
        updateAXNUIContentMockRPFieldValue("denyMessageHTML", inheritedAXNUIContent.mockRP.default.denyMessageHTML);
    };

    const handleToggleCaptureTransactionChange = (newValue: string) => {
        updateAXNUILayoutPropertyValue({
            ...axnuiLayout,
            mockRP: {
                ...axnuiLayout.mockRP,
                [redirectUrl]: {
                    showAsiLabel: isStringTrue(newValue)
                }
            }
        });
    };

    const handleResetCaptureTransactionMessage = () => {
        let showAsiLabel = inheritedAXNUILayout.mockRP.default.showAsiLabel;
        if (inheritedAXNUILayout.mockRP[redirectUrl]) {
            showAsiLabel = inheritedAXNUILayout.mockRP[redirectUrl].showAsiLabel;
        }
        updateAXNUILayoutPropertyValue({
            ...axnuiLayout,
            mockRP: {
                ...axnuiLayout.mockRP,
                [redirectUrl]: {
                    showAsiLabel
                }
            }
        });
    };

    const handleAsiLabelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentMockRPFieldValue("asiLabel", event.target.value);
    };

    const handleResetAsiLabel = () => {
        updateAXNUIContentMockRPFieldValue("asiLabel", inheritedAXNUIContent.mockRP.default.asiLabel);
    };

    const afterInputElement = useMemo(() => {
        return (
            <div className={styles.buttonContainer}>
                {showOriginBadge && (
                    <RedirectUrlOriginBadge redirectUrlOrigin={redirectUrlOrigin} />
                )}
                {!isDisabled && (<>
                    <ClickableIcon
                        controlId={`modify-custom-landing-page-${redirectUrl}-${index}`}
                        disabled={isDisabled}
                        icon={faPencilAlt}
                        onClick={handleClickModifyLandingPageName}
                        className={styles.afterInputIcon}
                    />
                    <ClickableIcon
                        controlId={`delete-custom-landing-page-${redirectUrl}-${index}`}
                        disabled={isDisabled}
                        icon={faTrash}
                        onClick={handleClickDeleteRedirectUrl}
                        className={styles.afterInputIcon}
                    />
                </>)}
            </div>
        );
    }, [
        isDisabled,
        redirectUrlOrigin,
        index,
        redirectUrl,
        showOriginBadge,
        handleClickModifyLandingPageName,
        handleClickDeleteRedirectUrl
    ]);


    const resource = resources.pages.verificationServiceDetails.tabs.theming.subTabs.landingPage;

    return (
        <div className={`${styles.customLandingPageContainer} ${isTheming ? styles.themingContainer : null}`}>
            <ThemingFormSection>
                <InputFormGroup
                    automationId={`custom-landing-page-name-input`}
                    controlId={`custom-landing-page-name-input`}
                    ariaLabel={resource.inputs.landingPage.label}
                    disabled={true}
                    isRequired={true}
                    onChange={() => { }}
                    value={redirectUrlForDisplay}
                    labelWidth={labelWidth}
                    layout={layout}
                    label={isTheming ? resource.inputs.landingPage.label : null}
                    afterInputElement={afterInputElement}
                />
                {isTheming && (
                    <>
                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedApproveMessageHTML}
                            currentThemingPropertyValue={approveMessageHTML}
                            pageLevel={pageLevel}
                        >
                            <TextareaFormGroup
                                controlId={`landing-page-${redirectUrl}-approve-message-html-input`}
                                automationId={`landing-page-${redirectUrl}-approve-message-html-input`}
                                label={resource.inputs.approveMessageHTML.label}
                                ariaLabel={resource.inputs.approveMessageHTML.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleApproveMessageHTMLChange}
                                value={approveMessageHTML || ""}
                                disabled={isDisabled}
                                isRequired={true}
                                afterLabelTooltipText={resource.inputs.approveMessageHTML.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetApproveMessageHTML}
                            />
                        </ThemingFormGroupWrapper>
                        {validationErrors.errorObject[redirectUrl]?.approveMessageHTMLError}
                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedDenyMessageHTML}
                            currentThemingPropertyValue={denyMessageHTML}
                            pageLevel={pageLevel}
                        >
                            <TextareaFormGroup
                                controlId={`landing-page-${redirectUrl}-deny-message-input`}
                                automationId={`landing-page-${redirectUrl}-deny-message-input`}
                                label={resource.inputs.denyMessageHTML.label}
                                ariaLabel={resource.inputs.denyMessageHTML.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleDenyMessageHTMLChange}
                                value={denyMessageHTML || ""}
                                disabled={isDisabled}
                                isRequired={true}
                                afterLabelTooltipText={resource.inputs.denyMessageHTML.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetDenyMessageHTML}
                            />
                        </ThemingFormGroupWrapper>
                        {validationErrors.errorObject[redirectUrl]?.denyMessageHTMLError}
                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedShowAsiLabel}
                            currentThemingPropertyValue={showAsiLabel}
                            pageLevel={pageLevel}
                        >
                            <RadioInputFormGroup
                                controlId={`theming-landing-page-${redirectUrl}-capture-transaction-radio-input`}
                                automationId={`theming-landing-page-${redirectUrl}-capture-transaction-radio-input`}
                                label={resource.inputs.showAsiLabel.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleToggleCaptureTransactionChange}
                                options={BOOLEAN_RADIO_INPUT_OPTIONS}
                                value={showAsiLabel ? "true" : "false"}
                                disabled={isDisabled}
                                isRequired={true}
                                afterLabelTooltipText={resource.inputs.showAsiLabel.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetCaptureTransactionMessage}
                            />

                        </ThemingFormGroupWrapper>
                        {showAsiLabel && (
                            <>
                                <ThemingFormGroupWrapper
                                    inheritedThemingPropertyValue={inheritedAsiLabel}
                                    currentThemingPropertyValue={asiLabel}
                                    pageLevel={pageLevel}
                                >
                                    <InputFormGroup
                                        controlId={`landing-page-${redirectUrl}-transaction-name-input`}
                                        automationId={`landing-page-${redirectUrl}-transaction-name-input`}
                                        label={resource.inputs.asiLabel.label}
                                        ariaLabel={resource.inputs.asiLabel.label}
                                        layout={layout}
                                        labelWidth={labelWidth}
                                        onChange={handleAsiLabelChange}
                                        value={asiLabel || ""}
                                        disabled={isDisabled}
                                        isRequired={true}
                                        afterLabelTooltipText={resource.inputs.asiLabel.tooltip}
                                        afterLabelIcon={isDisabled ? null : faUndo}
                                        afterLabelIconOnClick={handleResetAsiLabel}
                                    />
                                </ThemingFormGroupWrapper>
                                {validationErrors.errorObject[redirectUrl]?.asiLabelError}
                            </>
                        )}
                    </>
                )}

            </ThemingFormSection>
        </div>
    );
};